import type {Configuration} from '../configuration';
import type {AxiosInstance, AxiosPromise, RawAxiosRequestConfig} from 'axios';
import {BASE_PATH, BaseAPI, operationServerMap, RequestArgs} from '../base';
import globalAxios from 'axios';
import {
  assertParamExists,
  createRequestFunction,
  DUMMY_BASE_URL,
  serializeDataIfNeeded,
  setSearchParams,
  toPathString,
} from '../common';

/**
 * User registration request for self-registering organization
 * @export
 * @interface UserRegisterOrganizationReq
 */
export interface UserRegisterOrganizationReq {
  /**
   * User email
   * @type {string}
   * @memberof UserRegisterOrganizationReq
   */
  email: string;
  /**
   * User first name
   * @type {string}
   * @memberof UserRegisterOrganizationReq
   */
  firstName: string;
  /**
   * User last name
   * @type {string}
   * @memberof UserRegisterOrganizationReq
   */
  lastName: string;
  /**
   * User password
   * @type {string}
   * @memberof UserRegisterOrganizationReq
   */
  password: string;
  /**
   * Organization name
   * @type {string}
   * @memberof UserRegisterOrganizationReq
   */
  organizationName: string;
  /**
   * Organization description
   * @type {string}
   * @memberof UserRegisterOrganizationReq
   */
  organizationDescription: string;
}

/**
 * User registration response
 * @export
 * @interface UserRegisterOrganizationResponse
 */
export interface UserRegisterOrganizationResponse {
  /**
   * Success message
   * @type {string}
   * @memberof UserRegisterOrganizationResponse
   */
  message: string;
  /**
   * User and organization data
   * @type {object}
   * @memberof UserRegisterOrganizationResponse
   */
  data: {
    /**
     * User ID
     * @type {string}
     */
    userId: string;
    /**
     * User email
     * @type {string}
     */
    email: string;
    /**
     * User status
     * @type {string}
     */
    status: string;
    /**
     * Verification sent flag
     * @type {boolean}
     */
    verificationSent: boolean;
    /**
     * Organization data
     * @type {object}
     */
    organization: {
      /**
       * Organization ID
       * @type {string}
       */
      id: string;
      /**
       * Organization name
       * @type {string}
       */
      name: string;
      /**
       * Organization code
       * @type {string}
       */
      code: string;
      /**
       * Organization description
       * @type {string}
       */
      description: string;
    };
  };
}

/**
 * Email verification request
 * @export
 * @interface UserVerifyEmailReq
 */
export interface UserVerifyEmailReq {
  /**
   * User ID
   * @type {string}
   * @memberof UserVerifyEmailReq
   */
  userId: string;
  /**
   * Verification token
   * @type {string}
   * @memberof UserVerifyEmailReq
   */
  token: string;
}

/**
 * Email verification response
 * @export
 * @interface UserVerifyEmailResponse
 */
export interface UserVerifyEmailResponse {
  /**
   * Success message
   * @type {string}
   * @memberof UserVerifyEmailResponse
   */
  message: string;
  /**
   * User and organization data
   * @type {object}
   * @memberof UserVerifyEmailResponse
   */
  data: {
    /**
     * User data
     * @type {object}
     */
    user: {
      /**
       * User ID
       * @type {string}
       */
      id: string;
      /**
       * User email
       * @type {string}
       */
      email: string;
      /**
       * User first name
       * @type {string}
       */
      firstName: string;
      /**
       * User last name
       * @type {string}
       */
      lastName: string;
      /**
       * User status
       * @type {string}
       */
      status: string;
    };
    /**
     * Organization data
     * @type {object}
     */
    organization: {
      /**
       * Organization ID
       * @type {string}
       */
      id: string;
      /**
       * Organization name
       * @type {string}
       */
      name: string;
      /**
       * Organization code
       * @type {string}
       */
      code: string;
      /**
       * Organization description
       * @type {string}
       */
      description: string;
    };
  };
}

/**
 * UserPublicServiceApi - axios parameter creator
 * @export
 */
export const UserPublicServiceApiAxiosParamCreator = function (configuration?: Configuration) {
  return {
    /**
     * Self register new organization - Public API that allows users to register themselves and create a new organization
     * @summary Self register new organization
     * @param {UserRegisterOrganizationReq} userRegisterOrganizationReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userPublicServiceRegisterOrganization: async (
      userRegisterOrganizationReq: UserRegisterOrganizationReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'userRegisterOrganizationReq' is not null or undefined
      assertParamExists(
        'userPublicServiceRegisterOrganization',
        'userRegisterOrganizationReq',
        userRegisterOrganizationReq
      );
      const localVarPath = `/user/register-organization`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        userRegisterOrganizationReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },

    /**
     * Verify email when user registers new organization - Public API for email verification
     * @summary Verify email when user registers new organization
     * @param {UserVerifyEmailReq} userVerifyEmailReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userPublicServiceVerifyEmail: async (
      userVerifyEmailReq: UserVerifyEmailReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'userVerifyEmailReq' is not null or undefined
      assertParamExists('userPublicServiceVerifyEmail', 'userVerifyEmailReq', userVerifyEmailReq);
      const localVarPath = `/user/verify-email`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        userVerifyEmailReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
  };
};

/**
 * UserPublicServiceApi - functional programming interface
 * @export
 */
export const UserPublicServiceApiFp = function (configuration?: Configuration) {
  const localVarAxiosParamCreator = UserPublicServiceApiAxiosParamCreator(configuration);
  return {
    /**
     * Self register new organization - Public API that allows users to register themselves and create a new organization
     * @summary Self register new organization
     * @param {UserRegisterOrganizationReq} userRegisterOrganizationReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userPublicServiceRegisterOrganization(
      userRegisterOrganizationReq: UserRegisterOrganizationReq,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserRegisterOrganizationResponse>
    > {
      const localVarAxiosArgs =
        await localVarAxiosParamCreator.userPublicServiceRegisterOrganization(
          userRegisterOrganizationReq,
          options
        );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserPublicServiceApi.userPublicServiceRegisterOrganization']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },

    /**
     * Verify email when user registers new organization - Public API for email verification
     * @summary Verify email when user registers new organization
     * @param {UserVerifyEmailReq} userVerifyEmailReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async userPublicServiceVerifyEmail(
      userVerifyEmailReq: UserVerifyEmailReq,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserVerifyEmailResponse>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.userPublicServiceVerifyEmail(
        userVerifyEmailReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['UserPublicServiceApi.userPublicServiceVerifyEmail']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
  };
};

/**
 * UserPublicServiceApi - factory interface
 * @export
 */
export const UserPublicServiceApiFactory = function (
  configuration?: Configuration,
  basePath?: string,
  axios?: AxiosInstance
) {
  const localVarFp = UserPublicServiceApiFp(configuration);
  return {
    /**
     * Self register new organization - Public API that allows users to register themselves and create a new organization
     * @summary Self register new organization
     * @param {UserRegisterOrganizationReq} userRegisterOrganizationReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userPublicServiceRegisterOrganization(
      userRegisterOrganizationReq: UserRegisterOrganizationReq,
      options?: any
    ): AxiosPromise<UserRegisterOrganizationResponse> {
      return localVarFp
        .userPublicServiceRegisterOrganization(userRegisterOrganizationReq, options)
        .then(request => request(axios, basePath));
    },

    /**
     * Verify email when user registers new organization - Public API for email verification
     * @summary Verify email when user registers new organization
     * @param {UserVerifyEmailReq} userVerifyEmailReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    userPublicServiceVerifyEmail(
      userVerifyEmailReq: UserVerifyEmailReq,
      options?: any
    ): AxiosPromise<UserVerifyEmailResponse> {
      return localVarFp
        .userPublicServiceVerifyEmail(userVerifyEmailReq, options)
        .then(request => request(axios, basePath));
    },
  };
};

/**
 * UserPublicServiceApi - object-oriented interface
 * @export
 * @class UserPublicServiceApi
 * @extends {BaseAPI}
 */
export class UserPublicServiceApi extends BaseAPI {
  /**
   * Self register new organization - Public API that allows users to register themselves and create a new organization
   * @summary Self register new organization
   * @param {UserRegisterOrganizationReq} userRegisterOrganizationReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserPublicServiceApi
   */
  public userPublicServiceRegisterOrganization(
    userRegisterOrganizationReq: UserRegisterOrganizationReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserPublicServiceApiFp(this.configuration)
      .userPublicServiceRegisterOrganization(userRegisterOrganizationReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * Verify email when user registers new organization - Public API for email verification
   * @summary Verify email when user registers new organization
   * @param {UserVerifyEmailReq} userVerifyEmailReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof UserPublicServiceApi
   */
  public userPublicServiceVerifyEmail(
    userVerifyEmailReq: UserVerifyEmailReq,
    options?: RawAxiosRequestConfig
  ) {
    return UserPublicServiceApiFp(this.configuration)
      .userPublicServiceVerifyEmail(userVerifyEmailReq, options)
      .then(request => request(this.axios, this.basePath));
  }
}
